import React, { useEffect, useState } from "react";
import { FieldArray, Form, FormikProvider, useFormik } from "formik";
import {
  Box,
  Button,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  Checkbox,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  addNewJob,
  createGoal,
  getAlltagnames,
  GoalFileUpload,
  updateGoal,
} from "../../../redux/features/appraisalSlice";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import dayjs from "dayjs";
import GlobalMultiSelectId from "../../../components/mui/GlobalMultiSelectId";
import { Icon } from "@iconify/react";
import { Bookmark } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const GoalForm = ({ goals, newJobFieldRequired, user, adminView }) => {
  const dispatch = useDispatch();
  const { loading, tagNames } = useSelector((state) => state.appraisal);
  const { enqueueSnackbar } = useSnackbar();
  const [checklist, setChecklist] = useState([]);
  const [fileName, setFileName] = useState("");
  const [actionType, setActionType] = useState("save");

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getAlltagnames());
  }, [dispatch]);

  const uploadFile = async (file, dispatch) => {
    try {
      const formData = new FormData();
      formData.append("incomeScreenshot", file);
      const response = await dispatch(GoalFileUpload(formData));
      return response.payload?.data?.filename || "";
    } catch (error) {
      console.error("File upload failed:", error);
      return "";
    }
  };

  const ValidationSchema = Yup.object().shape({
    name: Yup.string().required("Add a goal name"),
    description: Yup.string().required("Add a description"),
    startDate: Yup.string().nullable().required("Start date is required"),
    endDate: Yup.string().nullable().required("End date is required"),
    tagIds: Yup.array().min(1, "Select at least one tag"),
    jobs: Yup.array()
      .of(
        Yup.object().shape({
          jobName: Yup.string().required("Job name is required"),
          startDate: Yup.date().nullable().required("Start date is required"),
          endDate: Yup.date().nullable().required("End date is required"),
        })
      )
      .min(2, "Choose minimum two jobs"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: goals?.goal_name || "",
      description: goals?.goal_description || "",
      startDate: goals?.goal_start_date ? dayjs(goals.goal_start_date) : null,
      endDate: goals?.goal_end_date ? dayjs(goals.goal_end_date) : null,
      tagIds: Array.isArray(goals?.tags)
        ? goals?.tags.map((tag) => tag?.id)
        : [],
      jobs: goals?.jobs
        ? goals.jobs.map((job) => ({
            jobName: job.goal_job_name || "",
            startDate: job.goal_job_start_date
              ? dayjs(job.goal_job_start_date)
              : null,
            endDate: job.goal_job_end_date
              ? dayjs(job.goal_job_end_date)
              : null,
            jobID: job.goal_job_id || "",
          }))
        : [
            { jobName: "", startDate: null, endDate: null },
            { jobName: "", startDate: null, endDate: null },
          ],
      uploadedFiles: [""],
      uploadedFileNames: [""],
      attachmentUrls: [""],
      assignedTo: user?.userID ? user?.userID : null,
      goal_id: goals ? goals.goal_id : null,
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      const cleanedTagIds = [...new Set(values.tagIds)];
      const postData = [
        {
          name: values.name,
          description: values.description,
          tagIds: cleanedTagIds,
          endDate: dayjs(values.endDate).format("YYYY-MM-DD"),
          startDate: dayjs(values.startDate).format("YYYY-MM-DD"),
          jobs: values.jobs.map((job) => ({
            jobName: job.jobName,
            startDate: dayjs(job.startDate).format("YYYY-MM-DD"),
            endDate: dayjs(job.endDate).format("YYYY-MM-DD"),
          })),
          sendForApproval: 1,
          uploadedFiles: values.uploadedFiles,
          attachmentUrls: values.attachmentUrls,
          assignedTo: user?.userID ? user?.userID : null,
          // goal_id: goals ? goals.goal_id : null,
        },
      ];

      const updateData = {
        name: values.name,
        description: values.description,
        tagIds: cleanedTagIds,
        endDate: dayjs(values.endDate).format("YYYY-MM-DD"),
        startDate: dayjs(values.startDate).format("YYYY-MM-DD"),
        jobs: values.jobs.map((job) => ({
          jobName: job.jobName,
          startDate: dayjs(job.startDate).format("YYYY-MM-DD"),
          endDate: dayjs(job.endDate).format("YYYY-MM-DD"),
          goal_job_id: job.jobID,
        })),
        sendForApproval: 1,
        uploadedFiles: values.uploadedFiles,
        attachmentUrls: values.attachmentUrls,
        assignedTo: user?.userID ? user?.userID : null,
        goal_id: goals ? goals.goal_id : null,
      };

      const newJob = values.jobs
        .filter((job) => !job.jobID)
        .map((job) => ({
          goal_job_name: job.jobName,
          goal_job_start_date: dayjs(job.startDate).format("YYYY-MM-DD"),
          goal_job_end_date: dayjs(job.endDate).format("YYYY-MM-DD"),
        }));

      if (goals && !newJobFieldRequired) {
        dispatch(updateGoal(updateData)).then((res) => {
          if (res.payload.status === "success") {
            enqueueSnackbar("Goal updated Successfully", {
              variant: "success",
            });
            formik.resetForm();
            if (adminView) {
              navigate(-1);
            } else {
              navigate("/Appraisal-Management/appraisal");
            }

            setChecklist([]);
          } else {
            enqueueSnackbar(res.payload.message, { variant: "error" });
          }
        });
      } else if (!goals) {
        dispatch(createGoal(postData)).then((res) => {
          if (res.payload.status === "success") {
            enqueueSnackbar("Goal Saved Successfully", { variant: "success" });
            formik.resetForm();
            if (adminView) {
              navigate(-1);
            } else {
              navigate("/Appraisal-Management/appraisal");
            }

            setChecklist([]);
          } else {
            enqueueSnackbar(res.payload.message, { variant: "error" });
          }
        });
      } else {
        dispatch(
          addNewJob({
            data: newJob,
            goal_id: goals.goal_id,
            extendId: goals.last_extend_id,
          })
        ).then((res) => {
          if (res.payload.status === "success") {
            enqueueSnackbar("Goal updated Successfully", {
              variant: "success",
            });
            formik.resetForm();
            if (newJobFieldRequired) {
              navigate(-1);
            } else {
              navigate("/Appraisal-Management/appraisal");
            }

            setChecklist([]);
          } else {
            enqueueSnackbar(res.payload.message, { variant: "error" });
          }
        });
      }
    },
  });

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <Stack direction="column" spacing={2}>
          <TextField
            name="name"
            label="Goal Name"
            disabled={newJobFieldRequired}
            fullWidth
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
          <TextField
            name="description"
            label="Description"
            disabled={newJobFieldRequired}
            multiline
            rows={2}
            fullWidth
            value={formik.values.description}
            onChange={formik.handleChange}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            helperText={formik.touched.description && formik.errors.description}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                disabled={newJobFieldRequired}
                value={formik.values.startDate}
                onChange={(date) => formik.setFieldValue("startDate", date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error:
                      formik.touched.startDate &&
                      Boolean(formik.errors.startDate),
                    helperText:
                      formik.touched.startDate && formik.errors.startDate,
                  },
                }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="End Date"
                disabled={newJobFieldRequired}
                value={formik.values.endDate}
                minDate={formik.values.startDate}
                onChange={(date) => formik.setFieldValue("endDate", date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error:
                      formik.touched.endDate && Boolean(formik.errors.endDate),
                    helperText: formik.touched.endDate && formik.errors.endDate,
                  },
                }}
              />
            </LocalizationProvider>
          </Stack>

          <GlobalMultiSelectId
            label="Tag"
            arrayState={formik.values.tagIds}
            disabled={newJobFieldRequired}
            arraySetState={(val) => formik.setFieldValue("tagIds", val)}
            data={tagNames}
            fullWidth
            keys={{
              id: "appraisal_tag_name",
              value: "appraisal_tag_id",
            }}
          />
          {formik.errors.tagIds && (
            <Typography color="error" variant="caption">
              {formik.errors.tagIds}
            </Typography>
          )}
          <Typography variant="h6">Attachements</Typography>
          <Stack spacing={1} direction={"column"}>
            {formik.values.uploadedFiles.map((file, i) => (
              <Box key={i} display="flex" alignItems="center" gap={1}>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  sx={{ minWidth: 180 }}
                >
                  {file ? "Replace File" : "Upload File"}
                  <input
                    type="file"
                    hidden
                    accept="image/*,.pdf"
                    onChange={async (e) => {
                      const newFile = e.target.files[0];
                     if (newFile) {
  const fileKey = await uploadFile(newFile, dispatch);
  if (fileKey) {
    const updatedKeys = [...formik.values.uploadedFiles];
    const updatedNames = [...formik.values.uploadedFileNames];

    updatedKeys[i] = fileKey;
    updatedNames[i] = newFile.name; 

    formik.setFieldValue("uploadedFiles", updatedKeys);
    formik.setFieldValue("uploadedFileNames", updatedNames);
  }
}

                    }}
                  />
                </Button>
               <Typography variant="body2">
  {formik.values.uploadedFileNames[i]
    ? formik.values.uploadedFileNames[i]
    : "No file selected"}
</Typography>


                {formik.values.uploadedFiles.length > 1 && (
                  <Button
                    onClick={() => {
  const updatedFiles = formik.values.uploadedFiles.filter((_, index) => index !== i);
  const updatedNames = formik.values.uploadedFileNames.filter((_, index) => index !== i);
  formik.setFieldValue("uploadedFiles", updatedFiles);
  formik.setFieldValue("uploadedFileNames", updatedNames);
}}

                    color="error"
                  >
                    <Icon icon="material-symbols:delete-outline" width={18} />
                  </Button>
                )}

                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={() => {
  formik.setFieldValue("uploadedFiles", [...formik.values.uploadedFiles, ""]);
  formik.setFieldValue("uploadedFileNames", [...formik.values.uploadedFileNames, ""]);
}}

                >
                  <Icon icon="lets-icons:add" width={20} height={20} />
                </Button>
              </Box>
            ))}
          </Stack>
          <Stack spacing={1}>
            {formik.values.attachmentUrls.map((url, i) => (
              <Box key={i} display="flex" alignItems="center" gap={1}>
                <TextField
                  fullWidth
                  value={url}
                  size="small"
                  onChange={(e) => {
                    const updated = [...formik.values.attachmentUrls];
                    updated[i] = e.target.value;
                    formik.setFieldValue("attachmentUrls", updated);
                  }}
                  placeholder="Paste link"
                />
                {formik.values.attachmentUrls.length > 1 && (
                  <Button
                    onClick={() => {
                      const updated = formik.values.attachmentUrls.filter(
                        (_, index) => index !== i
                      );
                      formik.setFieldValue("attachmentUrls", updated);
                    }}
                    color="error"
                  >
                    <Icon icon="material-symbols:delete-outline" width={18} />
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={() =>
                    formik.setFieldValue("attachmentUrls", [
                      ...formik.values.attachmentUrls,
                      "",
                    ])
                  }
                >
                  <Icon icon="lets-icons:add" width={20} height={20} />
                </Button>
              </Box>
            ))}
          </Stack>

          <Box
            sx={{
              border: "1px solid #bababaff",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: 2,
            }}
          >
            <Typography variant="h6" mb={2}>
              Job list
            </Typography>
            <FieldArray
              name="jobs"
              render={(arrayHelpers) => (
                <>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    {formik.values.jobs.map((job, index) => {
                      const jobErrors = formik.errors.jobs?.[index] || {};
                      const jobTouched = formik.touched.jobs?.[index] || {};

                      return (
                        <Box
                          key={index}
                          display="flex"
                          alignItems="center"
                          flexDirection={{ xs: "column", md: "row" }}
                          gap={2}
                          mb={2}
                          width="100%"
                        >
                          <TextField
                            label="Job Name"
                            name={`jobs[${index}].jobName`}
                            value={job.jobName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            fullWidth
                            error={Boolean(
                              jobTouched.jobName && jobErrors.jobName
                            )}
                            helperText={jobTouched.jobName && jobErrors.jobName}
                          />
                          <DatePicker
                            label="Start Date"
                            value={job.startDate}
                            minDate={formik.values.startDate}
                            onChange={(date) =>
                              formik.setFieldValue(
                                `jobs[${index}].startDate`,
                                date
                              )
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: Boolean(
                                  jobTouched.startDate && jobErrors.startDate
                                ),
                                helperText:
                                  jobTouched.startDate && jobErrors.startDate,
                              },
                            }}
                          />
                          <DatePicker
                            label="End Date"
                            value={job.endDate}
                            minDate={job.startDate}
                            onChange={(date) =>
                              formik.setFieldValue(
                                `jobs[${index}].endDate`,
                                date
                              )
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: Boolean(
                                  jobTouched.endDate && jobErrors.endDate
                                ),
                                helperText:
                                  jobTouched.endDate && jobErrors.endDate,
                              },
                            }}
                          />
                          {formik.values.jobs.length > 2 && (
                            <Button
                              onClick={() => arrayHelpers.remove(index)}
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={user && goals}
                            >
                              <Icon
                                icon="material-symbols-light:delete"
                                width={20}
                                height={20}
                              />
                            </Button>
                          )}
                        </Box>
                      );
                    })}
                  </LocalizationProvider>

                  <Button
                    size="small"
                    sx={{ width: "fit-content" }}
                    onClick={() =>
                      arrayHelpers.push({
                        jobName: "",
                        startDate: null,
                        endDate: null,
                      })
                    }
                    disabled={user && goals}
                    variant="outlined"
                    color="secondary"
                  >
                    <Icon icon="lets-icons:add" width={20} height={20} />
                    Add Job
                  </Button>
                </>
              )}
            />
          </Box>
          {typeof formik.errors.jobs === "string" && (
            <Typography color="error" variant="caption">
              {formik.errors.jobs}
            </Typography>
          )}

          <Stack direction="row" spacing={2} mt={2}>
            {/* <Button
              size="medium"
              type="submit"
              fullWidth
              variant="outlined"
              color="secondary"
            >
              <Bookmark size={20} />
              {loading ? "Saving..." : "Save"}
            </Button> */}
            <Button
              size="medium"
              fullWidth
              variant="contained"
              type="submit"
              color="secondary"
            >
              <Icon icon="mingcute:send-fill" width={20} height={20} />
              {loading ? "Sending..." : "Send for Approval"}
            </Button>
          </Stack>
        </Stack>
      </Form>
    </FormikProvider>
  );
};

export default GoalForm;
